import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Printer } from 'lucide-react';
import { getMyGroups, GroupResponse } from '../logic/groupCreation/groupApi';
import { getGroupOverview, GroupOverviewResponse } from '../logic/groups/groupOverviewApi';
import { getGroupDassOverview, GroupDassOverviewResponse } from '../logic/groups/groupDassOverviewApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import StarRadarChart from '../components/StarRadarChart';
import { API_CONFIG } from '../config/api';
import { clampPercentage } from '../utils/scoringUtils';

type DataScope = 'consultant' | 'enterprise';

interface OverviewPageProps {
  scope?: DataScope;
}

interface SowbAttribute {
  id: string;
  name: string;
  score: number;
  trend: string;
  status: 'Good' | 'Fair' | 'Poor';
  description: string;
  icon: string;
}

const ConsultantOverviewPage: React.FC<OverviewPageProps> = ({ scope = 'consultant' }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const GROUP_STORAGE_KEY = 'consultantOverviewSelectedGroupId';
  const ASSESSMENT_MODE_STORAGE_KEY = 'consultantOverviewAssessmentMode';

  const [groups, setGroups] = useState<GroupResponse[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(scope === 'consultant');
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() =>
    localStorage.getItem(GROUP_STORAGE_KEY)
  );

  const [assessmentMode, setAssessmentMode] = useState<'dass21' | 'overall' | 'sowb'>(() => {
    const saved = localStorage.getItem(ASSESSMENT_MODE_STORAGE_KEY);
    return (saved as 'dass21' | 'overall' | 'sowb') || 'dass21';
  });
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [assessmentDropdownOpen, setAssessmentDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(new Date().getFullYear());

  const [overview, setOverview] = useState<GroupOverviewResponse['data'] | null>(null);
  const [dassOverview, setDassOverview] = useState<GroupDassOverviewResponse['data'] | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [assessmentHistory, setAssessmentHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const HISTORY_ITEMS_PER_PAGE = 5;
  
  // Personal assessment data (user's own scores, not group aggregates)
  const [personalMentalHealthPercentage, setPersonalMentalHealthPercentage] = useState<number | null>(null);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const groupSelector = document.querySelector('.consultant-overview__group-selector');
      const assessmentSelector = document.querySelector('.consultant-overview__assessment-mode-selector');
      const yearSelector = document.querySelector('.consultant-overview__year-selector');
      
      if (groupSelector && !groupSelector.contains(target)) {
        setGroupDropdownOpen(false);
      }
      if (assessmentSelector && !assessmentSelector.contains(target)) {
        setAssessmentDropdownOpen(false);
      }
      if (yearSelector && !yearSelector.contains(target)) {
        setYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist assessment mode to localStorage
  useEffect(() => {
    localStorage.setItem(ASSESSMENT_MODE_STORAGE_KEY, assessmentMode);
  }, [assessmentMode]);



  useEffect(() => {
    if (scope !== 'consultant') return;

    const fetchGroups = async () => {
      try {
        setGroupsLoading(true);
        setGroupsError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await getMyGroups(token, 1, 1000);
        const loadedGroups = response.data.groups || [];

        // Add "All groups" option at the beginning
        const groupsWithAll = [
          { id: 'all', groupName: 'All groups', createdBy: null } as any,
          ...loadedGroups
        ];

        setGroups(groupsWithAll);

        const stored = localStorage.getItem(GROUP_STORAGE_KEY);
        const storedStillValid = stored && loadedGroups.some((g) => g.id === stored);

        if (storedStillValid) {
          setSelectedGroupId(stored);
        } else if (loadedGroups.length > 0) {
          setSelectedGroupId(loadedGroups[0].id);
          localStorage.setItem(GROUP_STORAGE_KEY, loadedGroups[0].id);
        } else {
          setSelectedGroupId(null);
          localStorage.removeItem(GROUP_STORAGE_KEY);
        }
      } catch (e: any) {

        setGroups([]);
        setGroupsError(e?.message || 'Failed to load groups.');
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [navigate, scope]);

  useEffect(() => {
    if (scope !== 'consultant') return;
    if (!selectedGroupId) {
      setOverview(null);
      setDassOverview(null);
      return;
    }

    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        setOverviewError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Handle "All groups" case
        if (selectedGroupId === 'all') {
          const realGroups = groups.filter((g) => g.id !== 'all');
          
          if (realGroups.length === 0) {
            setOverview(null);
            setDassOverview(null);
            return;
          }

          if (assessmentMode === 'dass21') {
            // Fetch DASS-21 for all groups and average-of-averages
            const yearParam = selectedYear === 'all' ? undefined : selectedYear;
            const dassResponses = await Promise.all(
              realGroups.map((g) => getGroupDassOverview(token, g.id, yearParam))
            );

            const validResponses = dassResponses.filter((r) => r.data.counts.withAssessments > 0);
            
            if (validResponses.length === 0) {
              setDassOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: 0, withAssessments: 0 },
                averages: { overall0to42: 0, depression0to42: 0, anxiety0to42: 0, stress0to42: 0 },
                distribution: { low: 0, moderate: 0, high: 0 }
              });
              setOverview(null);
            } else {
              // Average the averages
              const avgOverall = validResponses.reduce((sum, r) => sum + r.data.averages.overall0to42, 0) / validResponses.length;
              const avgDepression = validResponses.reduce((sum, r) => sum + r.data.averages.depression0to42, 0) / validResponses.length;
              const avgAnxiety = validResponses.reduce((sum, r) => sum + r.data.averages.anxiety0to42, 0) / validResponses.length;
              const avgStress = validResponses.reduce((sum, r) => sum + r.data.averages.stress0to42, 0) / validResponses.length;
              const totalApproved = validResponses.reduce((sum, r) => sum + r.data.counts.approvedMembers, 0);
              const totalWithAssessments = validResponses.reduce((sum, r) => sum + r.data.counts.withAssessments, 0);
              const avgLow = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.low, 0) / validResponses.length);
              const avgModerate = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.moderate, 0) / validResponses.length);
              const avgHigh = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.high, 0) / validResponses.length);

              setDassOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: totalApproved, withAssessments: totalWithAssessments },
                averages: {
                  overall0to42: parseFloat(avgOverall.toFixed(1)),
                  depression0to42: parseFloat(avgDepression.toFixed(1)),
                  anxiety0to42: parseFloat(avgAnxiety.toFixed(1)),
                  stress0to42: parseFloat(avgStress.toFixed(1))
                },
                distribution: { low: avgLow, moderate: avgModerate, high: avgHigh }
              });
              setOverview(null);
            }
          } else {
            // Fetch SOWB/Overall for all groups and average-of-averages
            const yearParam = selectedYear === 'all' ? undefined : selectedYear;
            const overviewResponses = await Promise.all(
              realGroups.map((g) => getGroupOverview(token, g.id, yearParam))
            );

            const validResponses = overviewResponses.filter((r) => r.data.counts.withAssessments > 0);
            
            if (validResponses.length === 0) {
              setOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: 0, withAssessments: 0 },
                averages: { overall0to4: 0, attributes0to4: {}, attributesPercentage: {} },
                distribution: { low: 0, moderate: 0, high: 0 },
                trends: {}
              });
              setDassOverview(null);
            } else {
              // Average the averages for each attribute
              const attributes0to4: Record<string, number> = {};
              const allKeys = new Set<string>();
              
              validResponses.forEach((r) => {
                Object.keys(r.data.averages.attributes0to4 || {}).forEach((key) => allKeys.add(key));
              });

              allKeys.forEach((key) => {
                const sum = validResponses.reduce((acc, r) => acc + (r.data.averages.attributes0to4?.[key] || 0), 0);
                attributes0to4[key] = parseFloat((sum / validResponses.length).toFixed(2));
              });

              // Calculate attributesPercentage for multi-group case
              const attributesPercentage: Record<string, number> = {};
              const percentageKeys = new Set<string>();

              validResponses.forEach((r) => {
                Object.keys(r.data.averages.attributesPercentage || {}).forEach((key) => percentageKeys.add(key));
              });

              percentageKeys.forEach((key) => {
                const sum = validResponses.reduce((acc, r) => acc + (r.data.averages.attributesPercentage?.[key] || 0), 0);
                attributesPercentage[key] = Math.round(sum / validResponses.length);
              });

              const avgOverall = validResponses.reduce((sum, r) => sum + r.data.averages.overall0to4, 0) / validResponses.length;
              const totalApproved = validResponses.reduce((sum, r) => sum + r.data.counts.approvedMembers, 0);
              const totalWithAssessments = validResponses.reduce((sum, r) => sum + r.data.counts.withAssessments, 0);
              const avgLow = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.low, 0) / validResponses.length);
              const avgModerate = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.moderate, 0) / validResponses.length);
              const avgHigh = Math.round(validResponses.reduce((sum, r) => sum + r.data.distribution.high, 0) / validResponses.length);

              setOverview({
                group: { id: 'all', groupName: 'All groups', createdBy: null },
                counts: { approvedMembers: totalApproved, withAssessments: totalWithAssessments },
                averages: { overall0to4: parseFloat(avgOverall.toFixed(2)), attributes0to4, attributesPercentage },
                distribution: { low: avgLow, moderate: avgModerate, high: avgHigh },
                trends: {}
              });
              setDassOverview(null);
            }
          }
        } else {
          // Single group case
          const yearParam = selectedYear === 'all' ? undefined : selectedYear;
          if (assessmentMode === 'dass21') {
            const response = await getGroupDassOverview(token, selectedGroupId, yearParam);
            setDassOverview(response.data);
            setOverview(null);
          } else {
            const response = await getGroupOverview(token, selectedGroupId, yearParam, assessmentMode === 'sowb' ? 'sowb' : 'overall');
            setOverview(response.data);
            setDassOverview(null);
          }
        }
      } catch (e: any) {

        setOverview(null);
        setDassOverview(null);
        setOverviewError(e?.message || 'Failed to load group overview.');
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, [navigate, scope, selectedGroupId, assessmentMode, groups, selectedYear]);

  // Fetch assessment history for the group
  useEffect(() => {
    // Fetch for both SOWB and DASS-21 modes
    if (scope !== 'consultant' || !selectedGroupId) {
      setAssessmentHistory([]);
      setHistoryCurrentPage(1);
      return;
    }

    const fetchAssessmentHistory = async () => {
      try {
        setHistoryLoading(true);
        setHistoryCurrentPage(1);
        const token = localStorage.getItem('token');
        if (!token) return;
        const endpoint = assessmentMode === 'dass21' ? 'dass21-assessment-history' : 
                        assessmentMode === 'overall' ? 'overall-assessment-history' : 
                        'assessment-history';

        const yearParam = selectedYear === 'all' ? '' : `&year=${selectedYear}`;

        // Handle "all groups" case by fetching from all groups
        if (selectedGroupId === 'all') {
          const realGroups = groups.filter((g) => g.id !== 'all');
          if (realGroups.length === 0) {
            setAssessmentHistory([]);
            setHistoryLoading(false);
            return;
          }

          // Fetch history from all groups and aggregate
          const allHistories = await Promise.all(
            realGroups.map(async (g) => {
              try {
                const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/groups/${g.id}/${endpoint}?limit=200${yearParam}`;
                const response = await fetch(url, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                if (response.ok) {
                  const data = await response.json();
                  return data.data?.assessments || [];
                }
                return [];
              } catch (e) {
                console.error(`Error fetching history for group ${g.id}:`, e);
                return [];
              }
            })
          );

          // Flatten and sort by date (newest first)
          const combinedHistory = allHistories
            .flat()
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          // Validate: Ensure all assessments are from the selected year
          if (selectedYear !== 'all') {
            const allFromSelectedYear = combinedHistory.every((item: any) => 
              new Date(item.createdAt).getFullYear() === selectedYear
            );
            if (!allFromSelectedYear) {
              console.warn('[ConsultantOverviewPage] Backend returned assessments from multiple years. Applying frontend filter.');
            }
          }

          console.log(`Received ${combinedHistory.length} assessments from all groups for year ${selectedYear === 'all' ? 'all' : selectedYear}`, combinedHistory);
          setAssessmentHistory(combinedHistory);
        } else {
          // Single group case
          const baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/groups/${selectedGroupId}/${endpoint}`;
          const url = `${baseUrl}?limit=200${yearParam}`;
          
          console.log('Fetching assessment history from:', url);
          
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('Response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            const assessmentsArray = data.data?.assessments || [];
            
            // Validate: Ensure all assessments are from the selected year
            if (selectedYear !== 'all') {
              const allFromSelectedYear = assessmentsArray.every((item: any) => 
                new Date(item.createdAt).getFullYear() === selectedYear
              );
              if (!allFromSelectedYear) {
                console.warn('[ConsultantOverviewPage] Backend returned assessments from multiple years. Applying frontend filter.');
              }
            }
            
            console.log(`Received ${assessmentsArray.length} assessments for year ${selectedYear === 'all' ? 'all' : selectedYear}`, assessmentsArray);
            setAssessmentHistory(assessmentsArray);
          } else {
            console.error('Failed to fetch:', response.status, response.statusText);
            setAssessmentHistory([]);
          }
        }
      } catch (e) {
        // Silently fail for history - it's secondary data
        console.error('Error fetching assessment history:', e);
        setAssessmentHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchAssessmentHistory();
  }, [scope, selectedGroupId, overview, dassOverview, assessmentMode, selectedYear, groups]);

  // Fetch user's personal mental health assessment (like Dashboard does)
  useEffect(() => {
    const fetchPersonalMentalHealth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('[ConsultantOverviewPage] No token found, skipping personal mental health fetch');
          return;
        }

        const historyUrl = `${API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL}/history`;
        console.log('[ConsultantOverviewPage] Fetching personal mental health from:', historyUrl);

        const response = await fetch(historyUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          console.error('[ConsultantOverviewPage] Failed to fetch personal mental health:', response.status);
          setPersonalMentalHealthPercentage(null);
          return;
        }

        const data = await response.json();
        console.log('[ConsultantOverviewPage] Mental health response:', data);

        if (!data.success || !data.data) {
          console.log('[ConsultantOverviewPage] No mental health data in response');
          setPersonalMentalHealthPercentage(null);
          return;
        }

        let mhHistory = data.data || [];
        console.log('[ConsultantOverviewPage] Mental health history count (before filtering):', mhHistory.length);
        
        // Apply year filter if selectedYear is not 'all'
        if (selectedYear !== 'all') {
          const startDate = new Date(Date.UTC(selectedYear, 0, 1));
          const endDate = new Date(Date.UTC(selectedYear + 1, 0, 1));
          mhHistory = mhHistory.filter((a: any) => {
            const assessmentDate = new Date(a.createdAt);
            return assessmentDate >= startDate && assessmentDate < endDate;
          });
          console.log('[ConsultantOverviewPage] Mental health history count (after year filter for', selectedYear, '):', mhHistory.length);
        }
        
        // Helper to get latest by selectedCategory
        const getLatestByType = (assessments: any[], category: number) => {
          return assessments
            .filter(a => Number(a.selectedCategory) === category)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        };

        const dassLatest = getLatestByType(mhHistory, 1);
        const phqLatest = getLatestByType(mhHistory, 2);
        const gadLatest = getLatestByType(mhHistory, 3);
        const genderLatest = getLatestByType(mhHistory, 4);

        let dassPercent = null, phqPercent = null, gadPercent = null, genderPercent = null;
        
        if (dassLatest && dassLatest.scores) {
          dassPercent = Math.round(clampPercentage((dassLatest.scores.overall ?? 0) / 42 * 100));
          console.log('[ConsultantOverviewPage] DASS-21 score:', dassLatest.scores.overall, '→', dassPercent, '%');
        }
        if (phqLatest && phqLatest.scores) {
          phqPercent = Math.round(clampPercentage((phqLatest.scores.overall || 0) / 27 * 100));
          console.log('[ConsultantOverviewPage] PHQ-9 score:', phqLatest.scores.overall, '→', phqPercent, '%');
        }
        if (gadLatest && gadLatest.scores) {
          gadPercent = Math.round(clampPercentage((gadLatest.scores.overall || 0) / 21 * 100));
          console.log('[ConsultantOverviewPage] GAD-7 score:', gadLatest.scores.overall, '→', gadPercent, '%');
        }
        if (genderLatest && genderLatest.scores) {
          // Invert: higher raw means lower percent
          genderPercent = Math.round(clampPercentage(100 - ((genderLatest.scores.overall || 0) / 20 * 100)));
          console.log('[ConsultantOverviewPage] Gender Sensitivity score:', genderLatest.scores.overall, '→', genderPercent, '%');
        }

        // Only average the assessments that were actually taken (don't include untaken as 0%)
        const percentages = [];
        if (dassPercent !== null) percentages.push(dassPercent);
        if (phqPercent !== null) percentages.push(phqPercent);
        if (gadPercent !== null) percentages.push(gadPercent);
        if (genderPercent !== null) percentages.push(genderPercent);

        let combinedScore = 0;
        if (percentages.length > 0) {
          combinedScore = clampPercentage(Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length));
        }
        console.log('[ConsultantOverviewPage] Assessments taken:', percentages.length, '| Combined mental health score:', combinedScore, '%');
        
        setPersonalMentalHealthPercentage(combinedScore > 0 ? combinedScore : null);
      } catch (error) {
        console.error('[ConsultantOverviewPage] Error fetching personal mental health:', error);
        setPersonalMentalHealthPercentage(null);
      }
    };

    // Always fetch personal mental health (respects selectedYear)
    fetchPersonalMentalHealth();
  }, [selectedYear]);

  // Mock data for the scores table - different based on scope
  const getScoreData = (scope: DataScope) => {
    if (scope === 'enterprise') {
      // Enterprise-level data (organization-wide averages)
      return [
        { attribute: 'Spiritual', score: '7.8', trend: 'up' },
        { attribute: 'Nutritional', score: '6.9', trend: 'flat' },
        { attribute: 'Physical', score: '7.5', trend: 'up' },
        { attribute: 'Emotio-Psycho-Rational', score: '7.1', trend: 'up' },
        { attribute: 'Legendarial', score: '6.8', trend: 'down' },
        { attribute: 'Religio-Traditio-Cultural', score: '7.9', trend: 'up' },
        { attribute: 'Financial', score: '7.2', trend: 'flat' },
        { attribute: 'Environmental', score: '6.5', trend: 'down' },
        { attribute: 'Socio-Relational', score: '7.4', trend: 'up' },
        { attribute: 'Occupational', score: '7.3', trend: 'up' }
      ];
    }

    // Default consultant data (individual client)
    return [
      { attribute: 'Spiritual', score: '8.9', trend: 'up' },
      { attribute: 'Nutritional', score: '7.4', trend: 'up' },
      { attribute: 'Physical', score: '8.6', trend: 'up' },
      { attribute: 'Emotio-Psycho-Rational', score: '6.5', trend: 'down' },
      { attribute: 'Legendarial', score: '7.2', trend: 'flat' },
      { attribute: 'Religio-Traditio-Cultural', score: '8.8', trend: 'up' },
      { attribute: 'Financial', score: '6.2', trend: 'down' },
      { attribute: 'Environmental', score: '5.8', trend: 'down' },
      { attribute: 'Socio-Relational', score: '6.9', trend: 'flat' },
      { attribute: 'Occupational', score: '7.7', trend: 'up' }
    ];
  };

  // Filter assessment history based on assessment mode and selected year
  // This ensures ONLY data from the selected year is displayed
  const filteredAssessmentHistory = useMemo(() => {
    if (!assessmentHistory || assessmentHistory.length === 0) {
      return [];
    }

    if (selectedYear === 'all') {
      return assessmentHistory;
    }

    // Strictly filter to only include assessments from the selected year
    const filtered = assessmentHistory.filter((item) => {
      if (!item.createdAt) {
        console.warn('[ConsultantOverviewPage] Assessment item missing createdAt:', item);
        return false;
      }
      const itemYear = new Date(item.createdAt).getFullYear();
      const matches = itemYear === selectedYear;
      
      if (!matches) {
        console.debug(`[ConsultantOverviewPage] Filtering out assessment from year ${itemYear} (selected: ${selectedYear})`);
      }
      
      return matches;
    });

    return filtered;
  }, [assessmentHistory, selectedYear]);

  const axis = useMemo(
    () => [
      { key: 'spiritual', label: 'Spiritual', description: 'Meaning, purpose, and inner peace', icon: 'self_improvement' },
      { key: 'nutritional', label: 'Nutritional', description: 'Dietary habits and intake', icon: 'restaurant' },
      { key: 'physical', label: 'Physical', description: 'Activity levels and health', icon: 'fitness_center' },
      { key: 'emotioPsychoRational', label: 'Emotio-Psycho-Rational', description: 'Mental and emotional balance', icon: 'psychology' },
      { key: 'legendarial', label: 'Legendarial', description: 'Legacy and personal story', icon: 'history_edu' },
      { key: 'religioTraditioCultural', label: 'Religio-Traditio-Cultural', description: 'Cultural and traditional values', icon: 'account_balance' },
      { key: 'financial', label: 'Financial', description: 'Economic stability', icon: 'payments' },
      { key: 'environmental', label: 'Environmental', description: 'Living environment and surroundings', icon: 'nature' },
      { key: 'socioRelational', label: 'Socio-Relational', description: 'Relationships and community', icon: 'diversity_3' },
      { key: 'occupational', label: 'Occupational', description: 'Career and daily work', icon: 'work' }
    ],
    []
  );

  const display10 = (v0to4: number) => (v0to4 * 2.5);

  const scoreData = useMemo(() => {
    if (scope !== 'consultant') return getScoreData(scope);
    
    if (assessmentMode === 'dass21') {
      if (!dassOverview) {
        return [
          { attribute: 'Depression', score: '—', trend: 'flat' },
          { attribute: 'Anxiety', score: '—', trend: 'flat' },
          { attribute: 'Stress', score: '—', trend: 'flat' }
        ];
      }
      
      // Convert DASS-21 scores (0-42) to percentage (0-100)
      const depressionPct = dassOverview.averages?.depression0to42 ? (dassOverview.averages.depression0to42 / 42) * 100 : 0;
      const anxietyPct = dassOverview.averages?.anxiety0to42 ? (dassOverview.averages.anxiety0to42 / 42) * 100 : 0;
      const stressPct = dassOverview.averages?.stress0to42 ? (dassOverview.averages.stress0to42 / 42) * 100 : 0;
      
      return [
        { attribute: 'Depression', score: depressionPct.toFixed(1), trend: 'flat' },
        { attribute: 'Anxiety', score: anxietyPct.toFixed(1), trend: 'flat' },
        { attribute: 'Stress', score: stressPct.toFixed(1), trend: 'flat' }
      ];
    }
    
    if (!overview) {
      return axis.map((a) => ({
        attribute: a.label,
        score: '–',
        trend: 'flat'
      }));
    }

    return axis.map((a) => {
      const v0to4 = overview.averages?.attributes0to4?.[a.key] ?? 0;
      const score10 = display10(v0to4);
      const trend = overview.trends?.[a.key] ?? 'flat';

      return {
        attribute: a.label,
        score: score10 > 0 ? score10.toFixed(1) : '–',
        trend
      };
    });
  }, [axis, overview, dassOverview, scope, assessmentMode]);

  // Create chart data for overall assessment line graph
  // IMPORTANT: This chart displays data from the backend /groups/{id}/overview endpoint
  // For correct percentages matching the InsightsPage, the backend MUST:
  // 1. Fetch only the LATEST completed assessment per subcategory for each group member
  // 2. Calculate percentages using proper max scores for each assessment type
  // 3. Average those percentages across all group members
  // 4. Return in attributesPercentage field
  // 5. Respect the year parameter for filtering assessments
  //
  // If attributes0to4 is provided instead, it will be converted as: (score / 4) * 100
  const getCategoryColor = (categoryKey: string): string => {
    const colorMap: { [key: string]: string } = {
      spiritual: '#CDB4DB',
      nutritional: '#CDEAC0',
      physical: '#FFD6A5',
      emotioPsychoRational: '#BEE1E6',
      legendarial: '#FFB5C8',
      religioTraditioCultural: '#FFF1C1',
      financial: '#F9E79F',
      environmental: '#D4F1BE',
      socioRelational: '#E4C1F9',
      occupational: '#AEE2E0'
    };
    return colorMap[categoryKey] || '#25b09b';
  };

  // Custom dot component that uses category colors
  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    const color = payload.color || '#25b09b';
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={color}
        stroke={color}
        strokeWidth={2}
      />
    );
  };

  const CustomActiveDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    const color = payload.color || '#25b09b';
    return (
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill={color}
        stroke={color}
        strokeWidth={2}
      />
    );
  };

  const overallChartData = useMemo(() => {
    if (scope !== 'consultant' || !overview) {
      return [];
    }

    // Skip if in DASS21 mode (that has different chart)
    if (assessmentMode === 'dass21') {
      return [];
    }

    if (!overview.averages) {
      return [];
    }

    const chartData = axis.map((a) => {
      let percentage: number = 0;
      
      // For Emotio-Psycho-Rational, use personal score if available, otherwise use group aggregate
      if (a.key === 'emotioPsychoRational' && personalMentalHealthPercentage !== null) {
        percentage = personalMentalHealthPercentage;
        console.log('[overallChartData] Using personal mental health score:', percentage, '%');
      } else {
        // Use attributesPercentage from backend - this should match InsightsPage calculation
        const backendPercentage = overview.averages?.attributesPercentage?.[a.key];
        if (typeof backendPercentage === 'number') {
          percentage = backendPercentage;
          if (a.key === 'emotioPsychoRational') {
            console.log('[overallChartData] Using backend aggregated score for', a.key, ':', percentage, '%', '(personal:', personalMentalHealthPercentage, ')');
          }
        } else {
          // Fallback: Convert attributes0to4 to percentage if attributesPercentage not available
          const v0to4 = overview.averages?.attributes0to4?.[a.key];
          if (typeof v0to4 === 'number' && v0to4 >= 0) {
            percentage = (v0to4 / 4) * 100;
          }
        }
      }

      return {
        name: a.label,
        percentage: Math.round(percentage),
        key: a.key,
        color: getCategoryColor(a.key)
      };
    });

    return chartData;
  }, [axis, overview, scope, assessmentMode, personalMentalHealthPercentage]);

  const radar = useMemo(() => {
    const center = { x: 200, y: 200 };
    const maxR = 160;
    const step = (Math.PI * 2) / axis.length;

    const getV = (key: string) => {
      if (scope !== 'consultant' || !overview) return null;
      const v = overview.averages?.attributes0to4?.[key];
      return typeof v === 'number' ? v : null;
    };

    const points = axis.map((a, i) => {
      const angle = -Math.PI / 2 + step * i;
      const v0to4 = getV(a.key);
      const ratio =
        v0to4 !== null
          ? Math.max(0, Math.min(1, v0to4 / 4))
          : scope === 'enterprise'
            ? 0.75
            : 0;

      const x = center.x + Math.cos(angle) * maxR * ratio;
      const y = center.y + Math.sin(angle) * maxR * ratio;

      return { key: a.key, x, y };
    });

    return {
      polygonPoints: points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
      points
    };
  }, [axis, overview, scope]);

  const distribution = useMemo(() => {
    if (scope !== 'consultant') {
      return {
        total: 1240,
        high: 55,
        moderate: 30,
        low: 15,
        avgScore10: 7.8,
        dash: {
          low: 25,
          moderate: 75,
          high: 138,
          offModerate: -25,
          offHigh: -100
        }
      };
    }

    let overviewData = assessmentMode === 'dass21' ? dassOverview : overview;
    
    if (!overviewData) {
      return {
        total: 0,
        high: 0,
        moderate: 0,
        low: 0,
        avgScore10: 0,
        dash: {
          low: 0,
          moderate: 0,
          high: 0,
          offModerate: 0,
          offHigh: 0
        }
      };
    }

    const high = overviewData.distribution?.high ?? 0;
    const moderate = overviewData.distribution?.moderate ?? 0;
    const low = overviewData.distribution?.low ?? 0;
    const total = high + moderate + low;

    const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
    
    let avgScore10;
    if (assessmentMode === 'dass21') {
      // Convert DASS-21 overall (0-42) to percentage scale (0-100)
      avgScore10 = dassOverview?.averages?.overall0to42 ? (dassOverview.averages.overall0to42 / 42) * 100 : 0;
    } else {
      avgScore10 = display10(overview?.averages?.overall0to4 ?? 0);
    }

    const C = 251;
    const seg = (n: number) => (total > 0 ? (n / total) * C : 0);

    const lowLen = seg(low);
    const moderateLen = seg(moderate);
    const highLen = seg(high);

    return {
      total,
      high: pct(high),
      moderate: pct(moderate),
      low: pct(low),
      avgScore10: avgScore10 > 0 ? Number(avgScore10.toFixed(1)) : 0,
      dash: {
        low: lowLen,
        moderate: moderateLen,
        high: highLen,
        offModerate: -lowLen,
        offHigh: -(lowLen + moderateLen)
      }
    };
  }, [overview, dassOverview, scope, assessmentMode]);

  const sowbAttributes: SowbAttribute[] = useMemo(() => {
    if (scope === 'consultant' && assessmentMode === 'dass21') {
      if (!dassOverview) {
        return [
          { id: 'depression', name: 'Depression', score: 0, trend: 'flat' as const, status: 'Fair' as const, description: 'DASS-21 Depression Scale', icon: 'mood_bad' },
          { id: 'anxiety', name: 'Anxiety', score: 0, trend: 'flat' as const, status: 'Fair' as const, description: 'DASS-21 Anxiety Scale', icon: 'warning' },
          { id: 'stress', name: 'Stress', score: 0, trend: 'flat' as const, status: 'Fair' as const, description: 'DASS-21 Stress Scale', icon: 'sentiment_very_dissatisfied' }
        ];
      }

      const getStatus = (score: number): 'Good' | 'Fair' | 'Poor' => {
        // DASS-21: higher score = worse. Low = 0-14, Moderate = 15-30, High = 31+
        if (score < 15) return 'Good';
        if (score <= 30) return 'Fair';
        return 'Poor';
      };

      return [
        { id: 'depression', name: 'Depression', score: dassOverview.averages?.depression0to42 || 0, trend: 'flat' as const, status: getStatus(dassOverview.averages?.depression0to42 || 0), description: 'DASS-21 Depression Scale', icon: 'mood_bad' },
        { id: 'anxiety', name: 'Anxiety', score: dassOverview.averages?.anxiety0to42 || 0, trend: 'flat' as const, status: getStatus(dassOverview.averages?.anxiety0to42 || 0), description: 'DASS-21 Anxiety Scale', icon: 'warning' },
        { id: 'stress', name: 'Stress', score: dassOverview.averages?.stress0to42 || 0, trend: 'flat' as const, status: getStatus(dassOverview.averages?.stress0to42 || 0), description: 'DASS-21 Stress Scale', icon: 'sentiment_very_dissatisfied' }
      ];
    }

    if (scope !== 'consultant' || !overview) {
      // Return mock data for enterprise or when no overview data
      return axis.map((a) => ({
        id: a.key,
        name: a.label,
        score: 7.5, // Mock score
        trend: 'flat' as const, // Mock trend
        status: 'Fair' as const,
        description: a.description,
        icon: a.icon
      }));
    }

    return axis.map((a) => {
      let score10: number;
      let statusValue: 'Good' | 'Fair' | 'Poor';

      // For Emotio-Psycho-Rational, use personal percentage score if available
      if (a.key === 'emotioPsychoRational' && personalMentalHealthPercentage !== null) {
        // Convert percentage (0-100) to 0-10 scale for display
        score10 = (personalMentalHealthPercentage / 100) * 10;
        // Determine status from percentage
        statusValue = personalMentalHealthPercentage >= 67 ? 'Good' : personalMentalHealthPercentage >= 50 ? 'Fair' : 'Poor';
      } else {
        const v0to4 = overview.averages?.attributes0to4?.[a.key] ?? 0;
        score10 = display10(v0to4);
        // Derive status from score10 using same thresholds as plan
        statusValue = score10 >= 6.25 ? 'Good' : score10 >= 3.75 ? 'Fair' : 'Poor';
      }

      const trend = overview.trends?.[a.key] ?? 'flat';

      return {
        id: a.key,
        name: a.label,
        score: score10 > 0 ? Number(score10.toFixed(1)) : 0,
        trend: trend, // Use the trend string directly
        status: statusValue,
        description: a.description,
        icon: a.icon
      };
    });
  }, [axis, overview, dassOverview, scope, assessmentMode, personalMentalHealthPercentage]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Good':
        return 'emerald';
      case 'Fair':
        return 'amber';
      case 'Poor':
        return 'rose';
      default:
        return 'gray';
    }
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return 'trending_up';
    if (trend === 'down') return 'trending_down';
    return 'trending_flat';
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'up') return 'emerald';
    if (trend === 'down') return 'rose';
    return 'gray';
  };

  return (
    <div className={`consultant-overview ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-overview__container">
        {/* Header Section */}
        <div className="consultant-overview__header">
          <div className="consultant-overview__header-info">
            <h1 className="consultant-overview__title">
              Combined Well-being Overview
            </h1>
            <p className="consultant-overview__subtitle">
              {`Group: ${selectedGroup?.groupName || '–'}`} <span className="consultant-overview__separator">|</span> Period: 2026
            </p>
          </div>
          <div className="consultant-overview__header-controls">
            <div className="consultant-overview__controls-row">
              {scope === 'consultant' && (
                <>
                  <div className="consultant-overview__control-group consultant-overview__group-selector">
                    <label className="consultant-overview__control-label">
                      <span className="material-symbols-outlined">group</span>
                      <span>Group</span>
                    </label>
                    <div className="consultant-overview__client-selector">
                      <button
                        className={`consultant-overview__dropdown-btn ${selectedGroupId && selectedGroupId !== 'all' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                        onClick={() => setGroupDropdownOpen(!groupDropdownOpen)}
                        disabled={groupsLoading || groups.length === 0}
                      >
                        <span className="consultant-overview__dropdown-btn-text">
                          {groupsLoading ? 'Loading groups...' : selectedGroup?.groupName || 'Select group'}
                        </span>
                        <span className="consultant-overview__dropdown-icon">
                          <span className="material-symbols-outlined">expand_more</span>
                        </span>
                      </button>
                      {groupDropdownOpen && (
                        <div className="consultant-overview__dropdown-menu">
                          {groups.map((g) => (
                            <button
                              key={g.id}
                              className={`consultant-overview__dropdown-item ${selectedGroupId === g.id ? 'consultant-overview__dropdown-item--selected' : ''}`}
                              onClick={() => {
                                setSelectedGroupId(g.id);
                                localStorage.setItem(GROUP_STORAGE_KEY, g.id);
                                setGroupDropdownOpen(false);
                              }}
                            >
                              {g.groupName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="consultant-overview__control-group">
                    <label className="consultant-overview__control-label">
                      <span className="material-symbols-outlined">assessment</span>
                      <span>Assessment Type</span>
                    </label>
                    <div className="consultant-overview__assessment-mode-selector">
                      <button
                        className={`consultant-overview__dropdown-btn ${assessmentMode === 'overall' ? 'consultant-overview__dropdown-btn--active' : ''}`}
                        onClick={() => setAssessmentDropdownOpen(!assessmentDropdownOpen)}
                      >
                        <span className="consultant-overview__dropdown-btn-text">
                          {assessmentMode === 'dass21' ? 'DASS-21' : assessmentMode === 'overall' ? 'Overall Assessment' : 'SOWB'}
                        </span>
                        <span className="consultant-overview__dropdown-icon">
                          <span className="material-symbols-outlined">expand_more</span>
                        </span>
                      </button>
                      {assessmentDropdownOpen && (
                        <div className="consultant-overview__dropdown-menu">
                          <button
                            className={`consultant-overview__dropdown-item ${assessmentMode === 'overall' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                            onClick={() => {
                              setAssessmentMode('overall');
                              setAssessmentDropdownOpen(false);
                            }}
                          >
                            Overall Assessment
                          </button>
                          <button
                            className={`consultant-overview__dropdown-item ${assessmentMode === 'dass21' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                            onClick={() => {
                              setAssessmentMode('dass21');
                              setAssessmentDropdownOpen(false);
                            }}
                          >
                            DASS-21
                          </button>
                          <button
                            className={`consultant-overview__dropdown-item ${assessmentMode === 'sowb' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                            onClick={() => {
                              setAssessmentMode('sowb');
                              setAssessmentDropdownOpen(false);
                            }}
                          >
                            SOWB
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="consultant-overview__control-group">
                <label className="consultant-overview__control-label">
                  <span className="material-symbols-outlined">calendar_month</span>
                  <span>Time Period</span>
                </label>
                <div className="consultant-overview__year-selector">
                  <button 
                    className="consultant-overview__period-btn"
                    onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
                  >
                    <span>{selectedYear === 'all' ? 'All Time' : selectedYear}</span>
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                  {yearDropdownOpen && (
                    <div className="consultant-overview__year-dropdown-menu">
                      <button
                        className={`consultant-overview__dropdown-item ${selectedYear === 'all' ? 'consultant-overview__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedYear('all');
                          setYearDropdownOpen(false);
                        }}
                      >
                        All Time
                      </button>
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <button
                          key={year}
                          className={`consultant-overview__dropdown-item ${selectedYear === year ? 'consultant-overview__dropdown-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedYear(year);
                            setYearDropdownOpen(false);
                          }}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {scope === 'consultant' && !groupsLoading && groups.length === 0 ? (
          <div className="consultant-overview__empty-state">
            <div className="consultant-overview__card">
              <div className="consultant-overview__empty-state-content">
                <h3 className="consultant-overview__card-title">No groups created yet</h3>
                <p className="consultant-overview__card-subtitle">
                  Create a group to view group-wide well-being averages.
                </p>
                <Link className="btn btn-primary consultant-overview__empty-state-cta" to="/panel/consultant/group-creation">
                  Go to Group Creation
                </Link>
              </div>
              {groupsError && (
                <p className="consultant-overview__empty-state-error">
                  {groupsError}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {(scope === 'consultant' && (overviewLoading || overviewError)) && (
              <div className="consultant-overview__status-row">
                {overviewLoading && <span className="consultant-overview__status-text">Loading overview...</span>}
                {overviewError && <span className="consultant-overview__status-text consultant-overview__status-text--error">{overviewError}</span>}
              </div>
            )}

            {/* Main Grid Layout */}
            <div className="consultant-overview__grid">
          {/* Line Chart Section - Overall Assessment */}
          {assessmentMode === 'overall' && (
            <div className="consultant-overview__radar-section">
            <div className="consultant-overview__card">
              <div className="consultant-overview__card-header">
                <div>
                  <h3 className="consultant-overview__card-title">Overall Assessment Graph</h3>
                  <p className="consultant-overview__card-subtitle">Population Assessment across 10 dimensions</p>
                </div>
                <button className="consultant-overview__card-action">
                  <span className="material-symbols-outlined">more_horiz</span>
                </button>
              </div>
              {scope === 'consultant' && overview && overview.counts?.approvedMembers > 0 && overview.counts?.withAssessments === 0 && (
                <div className="consultant-overview__inline-empty">
                  No assessments found yet for approved members in this group.
                </div>
              )}
              {scope === 'consultant' && overview && overview.counts?.approvedMembers === 0 && (
                <div className="consultant-overview__inline-empty">
                  No approved members in this group yet.
                </div>
              )}
              {scope === 'consultant' && overview && overview.counts?.withAssessments > 0 && (
                <>
                  {overallChartData.length > 0 ? (
                    <div className="consultant-overview__line-chart-container">
                      <div className="consultant-overview__chart-info" style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e1' : '#888', marginBottom: '1rem', paddingLeft: '1rem' }}>
                        <strong>Data Period:</strong> {selectedYear === 'all' ? 'All Time' : `${selectedYear}`} • <strong>Assessments:</strong> {overview.counts?.withAssessments} member{overview.counts?.withAssessments !== 1 ? 's' : ''}
                      </div>
                      <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={overallChartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#475569' : '#d1d5db'} />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        fontSize={12}
                        stroke={isDarkMode ? '#94a3b8' : '#374151'}
                        tick={{ fill: isDarkMode ? '#cbd5e1' : '#374151' }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                        fontSize={12}
                        stroke={isDarkMode ? '#94a3b8' : '#374151'}
                        tick={{ fill: isDarkMode ? '#cbd5e1' : '#374151' }}
                      />
                      <Tooltip
                        formatter={(value) => `${value}%`}
                        labelFormatter={(label) => `Category: ${label}`}
                        contentStyle={{
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                          border: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          boxShadow: isDarkMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          color: isDarkMode ? '#cbd5e1' : '#1f2937'
                        }}
                        labelStyle={{ color: isDarkMode ? '#cbd5e1' : '#1f2937' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="percentage"
                        stroke="#25b09b"
                        strokeWidth={3}
                        dot={<CustomDot />}
                        activeDot={<CustomActiveDot />}
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  {/* Individual Category Percentages - Attribute Cards */}
                  <div className="consultant-overview__chart-percentages">
                    <h4 className="consultant-overview__percentages-title">Category Breakdown (%)</h4>
                    <div className="consultant-overview__category-attributes">
                      {overallChartData.map((item, index) => {
                        // Find axis entry for icon
                        const axisEntry = axis.find(a => a.key === item.key);
                        // Determine status based on category-specific thresholds from InsightsPage
                        // Most categories: 75% = High, 50% = Moderate, <50% = Low
                        // Financial: 70% = High, 40% = Moderate, <40% = Low (different rule)
                        let statusClass: string;
                        let statusLabel: string;
                        
                        if (item.key === 'financial') {
                          // Financial has its own thresholds
                          statusClass = item.percentage >= 70 ? 'high' : item.percentage >= 40 ? 'moderate' : 'low';
                          statusLabel = item.percentage >= 70 ? 'High' : item.percentage >= 40 ? 'Moderate' : 'Low';
                        } else {
                          // All other categories use standard thresholds
                          statusClass = item.percentage >= 75 ? 'high' : item.percentage >= 50 ? 'moderate' : 'low';
                          statusLabel = item.percentage >= 75 ? 'High' : item.percentage >= 50 ? 'Moderate' : 'Low';
                        }
                        
                        // Emotio-Psycho-Rational and Occupational have inverted color logic
                        // (higher/bad is red, lower/good is green - opposite of most assessments)
                        const isInvertedCategory = item.key === 'occupational' || item.key === 'emotioPsychoRational';
                        const statusClassWithModifier = isInvertedCategory 
                          ? `consultant-overview__category-status--${statusClass} consultant-overview__category-status--inverted`
                          : `consultant-overview__category-status--${statusClass}`;
                        
                        return (
                          <div key={index} className="consultant-overview__category-card">
                            <div className="consultant-overview__category-card-top">
                              <div className={`consultant-overview__category-icon-wrapper consultant-overview__category-icon-wrapper--${item.key}`}>
                                <span className="material-symbols-outlined">{axisEntry?.icon || 'star'}</span>
                              </div>
                              <h4 className="consultant-overview__category-name">{item.name}</h4>
                            </div>
                            <div className="consultant-overview__category-card-bottom">
                              {item.percentage !== null && item.percentage !== undefined ? (
                                <>
                                  <span className="consultant-overview__category-percentage">{item.percentage}%</span>
                                  <span className={`consultant-overview__category-status ${statusClassWithModifier}`}>
                                    {statusLabel}
                                  </span>
                                </>
                              ) : (
                                <span className="consultant-overview__category-no-data">No assessment taken</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                  ) : (
                    <div className="consultant-overview__inline-empty">
                      Unable to load assessment data. Please ensure members have completed assessments.
                    </div>
                  )}
                </>
              )}
            </div>
            </div>
            )}

            {/* Assessment History - For Overall Assessment Mode */}
            {assessmentMode === 'overall' && (
              <div className="consultant-overview__history-wrapper">
                <div className="consultant-overview__card consultant-overview__card--history">
                  <div className="consultant-overview__card-header">
                    <h3 className="consultant-overview__card-title">Recent Overall Assessments</h3>
                  </div>
                  {historyLoading ? (
                    <div className="consultant-overview__history-loading">
                      <p>Loading assessment history...</p>
                    </div>
                  ) : filteredAssessmentHistory && filteredAssessmentHistory.length > 0 ? (
                    <div className="consultant-overview__history-list">
                      {filteredAssessmentHistory.slice(0, HISTORY_ITEMS_PER_PAGE).map((item, index) => {
                        // Handle both SOWB and individual assessment formats
                        const score = item.overallScore !== undefined ? item.overallScore : 
                                     (item.scores?.overall !== undefined ? item.scores.overall : 0);
                        const assessmentType = item.type || item.assessmentType || 'Assessment';
                        
                        return (
                          <div key={index} className="consultant-overview__history-item">
                            <div className="consultant-overview__history-item-header">
                              <div className="consultant-overview__history-item-type">
                                <span className="material-symbols-outlined">assignment</span>
                                <span className="consultant-overview__history-item-name">
                                  {assessmentType}
                                </span>
                              </div>
                              <div className="consultant-overview__history-item-date">
                                {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : 'Date unknown'}
                              </div>
                            </div>
                            <div className="consultant-overview__history-item-details">
                              <span className="consultant-overview__history-item-user">
                                {item.userName || item.user_name || 'Unknown User'}
                              </span>
                              {typeof score === 'number' && (
                                <span className="consultant-overview__history-item-score">
                                  {Math.round(score)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="consultant-overview__inline-empty">
                      No assessment history found for overall assessments.
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* DASS-21 Section - For DASS-21 Mode */}
          {assessmentMode === 'dass21' && scope === 'consultant' && (
            <div className="consultant-overview__dass-section">
              <div className="consultant-overview__card">
                <div className="consultant-overview__card-header">
                  <div>
                    <h3 className="consultant-overview__card-title">DASS-21 Assessment Breakdown</h3>
                    <p className="consultant-overview__card-subtitle">Depression, Anxiety, Stress Scales (0-42)</p>
                  </div>
                </div>
                {dassOverview && dassOverview.counts?.withAssessments > 0 ? (
                  <div className="consultant-overview__dass-metrics">
                    {sowbAttributes.map((attr) => (
                      <div key={attr.id} className="consultant-overview__dass-metric-card">
                        <div className="consultant-overview__dass-metric-header">
                          <span className="material-symbols-outlined">{attr.icon}</span>
                          <h4 className="consultant-overview__dass-metric-name">{attr.name}</h4>
                        </div>
                        <div className="consultant-overview__dass-metric-body">
                          <div className={`consultant-overview__dass-metric-score consultant-overview__dass-metric-score--${attr.status.toLowerCase()}`}>
                            {attr.score.toFixed(1)}
                          </div>
                          <div className="consultant-overview__dass-metric-scale">
                            <span>0</span>
                            <span>21</span>
                            <span>42</span>
                          </div>
                          <span className={`consultant-overview__dass-metric-status consultant-overview__dass-metric-status--${attr.status.toLowerCase()}`}>
                            {attr.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="consultant-overview__inline-empty">
                    {dassOverview?.counts?.approvedMembers === 0
                      ? 'No approved members in this group yet.'
                      : 'No DASS-21 assessments found yet for approved members.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SOWB Section - For SOWB Mode */}
          {assessmentMode === 'sowb' && scope === 'consultant' && (
            <div className="consultant-overview__sowb-section">
              <div className="consultant-overview__card">
                <div className="consultant-overview__card-header">
                  <div>
                    <h3 className="consultant-overview__card-title">Star of Well-Being Analysis</h3>
                    <p className="consultant-overview__card-subtitle">10-Dimensional Assessment across Approved Members</p>
                  </div>
                  <Link to="/panel/consultant/sowb-breakdown" className="consultant-overview__table-link">
                    View Full Breakdown
                  </Link>
                </div>
                {overview && overview.counts?.withAssessments > 0 ? (
                  <div className="consultant-overview__sowb-content">
                    {/* Chart Section */}
                    <div className="consultant-overview__sowb-chart">
                      <StarRadarChart
                        scores={overview.averages?.attributes0to4 || {}}
                        chartMode="filled"
                        size="full"
                        attributeLabels={{
                          spiritual: 'Spiritual',
                          nutritional: 'Nutritional',
                          physical: 'Physical',
                          emotioPsychoRational: 'Emotio-Psycho-Rational',
                          legendarial: 'Legendarial',
                          religioTraditioCultural: 'Religio-Traditio-Cultural',
                          financial: 'Financial',
                          environmental: 'Environmental',
                          socioRelational: 'Socio-Relational',
                          occupational: 'Occupational'
                        }}
                      />
                    </div>

                    {/* Attributes Grid */}
                    <div className="consultant-overview__sowb-attributes">
                      {(() => {
                        const attributeNames: { [key: string]: string } = {
                          spiritual: 'Spiritual',
                          nutritional: 'Nutritional',
                          physical: 'Physical',
                          emotioPsychoRational: 'Emotio-Psycho-Rational',
                          legendarial: 'Legendarial',
                          religioTraditioCultural: 'Religio-Traditio-Cultural',
                          financial: 'Financial',
                          environmental: 'Environmental',
                          socioRelational: 'Socio-Relational',
                          occupational: 'Occupational'
                        };

                        const getAttributeIcon = (key: string): string => {
                          const icons: { [key: string]: string } = {
                            spiritual: 'self_improvement',
                            nutritional: 'restaurant',
                            physical: 'fitness_center',
                            emotioPsychoRational: 'psychology',
                            legendarial: 'history_edu',
                            religioTraditioCultural: 'account_balance',
                            financial: 'payments',
                            environmental: 'nature',
                            socioRelational: 'diversity_3',
                            occupational: 'work'
                          };
                          return icons[key] || 'star';
                        };

                        const attributeOrder = [
                          'spiritual', 'nutritional', 'physical', 'emotioPsychoRational', 'legendarial',
                          'religioTraditioCultural', 'financial', 'environmental', 'socioRelational', 'occupational'
                        ];

                        return attributeOrder.map((key) => {
                          const score = overview.averages?.attributes0to4?.[key] ?? 0;
                          const roundedScore = Math.round(score); // Round to nearest integer (0-4)
                          const statusClass = roundedScore >= 4 ? 'excellent' : roundedScore >= 3 ? 'good' : roundedScore >= 2 ? 'fair' : 'poor';
                          const statusLabel = roundedScore >= 4 ? 'Excellent' : roundedScore >= 3 ? 'Good' : roundedScore >= 2 ? 'Fair' : 'Poor';

                          return (
                            <div key={key} className="consultant-overview__sowb-attribute-card">
                              <div className="consultant-overview__sowb-attribute-top">
                                <div className={`consultant-overview__sowb-attribute-icon-wrapper consultant-overview__sowb-attribute-icon-wrapper--${key}`}>
                                  <span className="material-symbols-outlined">{getAttributeIcon(key)}</span>
                                </div>
                                <h4 className="consultant-overview__sowb-attribute-name">{attributeNames[key]}</h4>
                              </div>
                              <div className="consultant-overview__sowb-attribute-bottom">
                                <span className="consultant-overview__sowb-attribute-score">{roundedScore}/4</span>
                                <span className={`consultant-overview__sowb-attribute-status consultant-overview__sowb-attribute-status--${statusClass}`}>
                                  {statusLabel}
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="consultant-overview__inline-empty">
                    {overview?.counts?.approvedMembers === 0
                      ? 'No approved members in this group yet.'
                      : 'No SOWB assessments found yet for approved members.'}
                  </div>
                )}
              </div>
            </div>
          )}

            {/* Assessment History - Full Width Below Grid (SOWB & DASS-21) */}
          {(assessmentMode === 'sowb' || assessmentMode === 'dass21') && (
          <div className="consultant-overview__history-full-width">
            <div className="consultant-overview__card">
              <div className="consultant-overview__card-header">
                <div>
                  <h3 className="consultant-overview__card-title">
                    {assessmentMode === 'dass21' ? 'DASS-21 Assessment History' : 'Star of Well-being Assessment History'}
                  </h3>
                  <p className="consultant-overview__card-subtitle">
                    {assessmentMode === 'dass21' 
                      ? 'Latest DASS-21 assessments of the consultant\'s group users' 
                      : 'Latest assessments of the consultant\'s group users'}
                  </p>
                </div>
              </div>
              {historyLoading ? (
                <div className="consultant-overview__inline-empty">Loading assessment history...</div>
              ) : filteredAssessmentHistory && filteredAssessmentHistory.length > 0 ? (
                <>
                  <div className="consultant-overview__history-table">
                    <div className="consultant-overview__history-table-header">
                      <div className="consultant-overview__history-table-col consultant-overview__history-table-col--name">User Name</div>
                      <div className="consultant-overview__history-table-col consultant-overview__history-table-col--date">Assessment Date</div>
                      <div className="consultant-overview__history-table-col consultant-overview__history-table-col--score">Score</div>
                      <div className="consultant-overview__history-table-col consultant-overview__history-table-col--level">Well-being Level</div>
                    </div>
                    {filteredAssessmentHistory
                      .slice(
                        (historyCurrentPage - 1) * HISTORY_ITEMS_PER_PAGE,
                        historyCurrentPage * HISTORY_ITEMS_PER_PAGE
                      )
                      .map((item, index) => (
                        <div key={`${item.id || index}`} className="consultant-overview__history-table-row">
                          <div className="consultant-overview__history-table-col consultant-overview__history-table-col--name">
                            {item.userName || 'User'}
                          </div>
                          <div className="consultant-overview__history-table-col consultant-overview__history-table-col--date">
                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '–'}
                          </div>
                          <div className="consultant-overview__history-table-col consultant-overview__history-table-col--score">
                            <span className="consultant-overview__history-score-badge">
                              {assessmentMode === 'dass21' 
                                ? (item.overallScore ? item.overallScore.toFixed(1) : '–')
                                : (item.overallScore ? Math.round(item.overallScore / 10) : '–')}
                              <span className="consultant-overview__history-score-unit">{assessmentMode === 'dass21' ? '/42' : '/4'}</span>
                            </span>
                          </div>
                          <div className="consultant-overview__history-table-col consultant-overview__history-table-col--level">
                            {item.overallLevel || (assessmentMode === 'dass21' ? 'DASS-21 Assessment' : 'SOWB10 Assessment')}
                          </div>
                        </div>
                      ))}
                  </div>
                  {/* Pagination Controls */}
                  {filteredAssessmentHistory.length > HISTORY_ITEMS_PER_PAGE && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', paddingBottom: '0.5rem' }}>
                      <button
                        onClick={() => setHistoryCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={historyCurrentPage === 1}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: historyCurrentPage === 1 ? (isDarkMode ? '#64748b' : '#d1d5db') : '#3b82f6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: historyCurrentPage === 1 ? 'not-allowed' : 'pointer',
                          opacity: historyCurrentPage === 1 ? 0.5 : 1
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: '0.9rem', color: isDarkMode ? '#94a3b8' : '#6b7280' }}>
                        Page {historyCurrentPage} of {Math.ceil(filteredAssessmentHistory.length / HISTORY_ITEMS_PER_PAGE)}
                      </span>
                      <button
                        onClick={() => setHistoryCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredAssessmentHistory.length / HISTORY_ITEMS_PER_PAGE)))}
                        disabled={historyCurrentPage >= Math.ceil(filteredAssessmentHistory.length / HISTORY_ITEMS_PER_PAGE)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: historyCurrentPage >= Math.ceil(filteredAssessmentHistory.length / HISTORY_ITEMS_PER_PAGE) ? (isDarkMode ? '#64748b' : '#d1d5db') : '#3b82f6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: historyCurrentPage >= Math.ceil(filteredAssessmentHistory.length / HISTORY_ITEMS_PER_PAGE) ? 'not-allowed' : 'pointer',
                          opacity: historyCurrentPage >= Math.ceil(filteredAssessmentHistory.length / HISTORY_ITEMS_PER_PAGE) ? 0.5 : 1
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="consultant-overview__inline-empty">
                  {scope === 'consultant' && selectedGroupId
                    ? 'No assessments found yet for this group.'
                    : 'Select a group to view assessment history.'}
                </div>
              )}
            </div>
          </div>
          )}

            </div>
          </>
        )}

        {/* Print-only Full Breakdown Section */}
        <div className="consultant-overview__print-breakdown">
          <h2 className="consultant-overview__print-breakdown-title">SOWB10 Detailed Breakdown</h2>
          <div className="consultant-overview__print-breakdown-table-container">
            <table className="consultant-overview__print-breakdown-table">
              <thead>
                <tr className="consultant-overview__print-breakdown-header">
                  <th className="consultant-overview__print-breakdown-th">Attribute</th>
                  <th className="consultant-overview__print-breakdown-th">Score (0-10)</th>
                  <th className="consultant-overview__print-breakdown-th">Trend</th>
                  <th className="consultant-overview__print-breakdown-th">Status</th>
                  <th className="consultant-overview__print-breakdown-th">Description</th>
                </tr>
              </thead>
              <tbody>
                {sowbAttributes.map((attribute) => (
                  <tr
                    key={attribute.id}
                    className="consultant-overview__print-breakdown-row"
                  >
                    <td className="consultant-overview__print-breakdown-td">
                      <div className="consultant-overview__print-breakdown-attribute">
                        <div className={`consultant-overview__print-breakdown-icon consultant-overview__print-breakdown-icon--${attribute.id}`}>
                          <span className="material-symbols-outlined">{attribute.icon}</span>
                        </div>
                        <span className="consultant-overview__print-breakdown-name">{attribute.name}</span>
                      </div>
                    </td>
                    <td className="consultant-overview__print-breakdown-td consultant-overview__print-breakdown-td--score">
                      {attribute.score.toFixed(1)}
                    </td>
                    <td className="consultant-overview__print-breakdown-td">
                      <div className={`consultant-overview__print-breakdown-trend consultant-overview__print-breakdown-trend--${getTrendColor(attribute.trend)}`}>
                        <span className="material-symbols-outlined">
                          {getTrendIcon(attribute.trend)}
                        </span>
                      </div>
                    </td>
                    <td className="consultant-overview__print-breakdown-td">
                      <span className={`consultant-overview__print-breakdown-status consultant-overview__print-breakdown-status--${getStatusColor(attribute.status)}`}>
                        {attribute.status}
                      </span>
                    </td>
                    <td className="consultant-overview__print-breakdown-td consultant-overview__print-breakdown-td--description">
                      {attribute.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <button
          className="consultant-overview__print-btn"
          onClick={() => window.print()}
          aria-label="Print page"
        >
          <Printer className="consultant-overview__print-icon" />
          Print
        </button>
      </div>
    </div>
  );
};

export default ConsultantOverviewPage;

